<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'users' }"
          >Users</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="my-6">
        <v-alert
          dense
          outlined
          type="warning"
          color="orange darken-3"
          max-width="400px"
          v-if="user && !!user.blocked_at"
        >
          This account is
          <strong>disabled</strong>.
        </v-alert>

        <avatar-form
          v-if="user"
          :user="user"
          @change="avatarChange"
          @avatarRemove="userAvatarRemoved"
          :avatarLoading="avatarLoading"
        />

        <avatar-form v-else @change="onUploadImage" />

        <v-form ref="form" class="user-form">
          <v-divider></v-divider>

          <h3 class="py-5 primary--text">{{ form.role }} Information</h3>

          <div class="d-flex">
            <div class="mr-3">
              <label class="text-field-label">First Name</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.first_name"
                :error-messages="form.$getError('first_name')"
                :loading="loading"
              ></v-text-field>
            </div>

            <div>
              <label class="text-field-label">Last Name</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.last_name"
                :error-messages="form.$getError('last_name')"
                :loading="loading"
              ></v-text-field>
            </div>
          </div>

          <label class="text-field-label">Email</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.email"
            :error-messages="form.$getError('email')"
            :loading="loading"
          >
            <v-tooltip slot="append" top v-if="user && user.email">
              <template v-slot:activator="{ on }">
                <v-icon color="success" v-on="on" v-if="user.email_verified">
                  <!-- eslint-disable-next-line -->
                  {{ icons.check }}
                </v-icon>
                <v-icon color="warning" v-on="on" v-else>
                  <!-- eslint-disable-next-line -->
                  {{ icons.alert }}
                </v-icon>
              </template>
              <span v-if="user.email_verified">Email Verified</span>
              <span v-else>Unverified email</span>
            </v-tooltip>
          </v-text-field>

          <label class="text-field-label">Phone number</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.phone_number"
            :error-messages="form.$getError('phone_number')"
            :loading="loading"
          >
            <v-tooltip slot="append" top v-if="user && user.phone_number">
              <template v-slot:activator="{ on }">
                <v-icon
                  color="success"
                  v-on="on"
                  v-if="user.phone_number_verified"
                  >{{ icons.check }}</v-icon
                >
                <v-icon color="warning" v-on="on" v-else>
                  <!-- eslint-disable-next-line -->
                  {{ icons.alert }}
                </v-icon>
              </template>
              <span v-if="user.phone_verified">Phone Number Verified</span>
              <span v-else>Unverified Phone Number</span>
            </v-tooltip>
          </v-text-field>

          <label class="text-field-label">Location</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.location"
            :error-messages="form.$getError('location')"
            :loading="loading"
          ></v-text-field>

          <label class="text-field-label">Display Name</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.display_name"
            :error-messages="form.$getError('display_name')"
            :loading="loading"
          ></v-text-field>

          <div class="d-flex">
            <div class="mr-3">
              <label class="text-field-label">Gender</label>
              <v-select
                class="mt-2"
                flat
                solo
                v-model="form.gender"
                :items="[
                  'Female',
                  'Non-Binary',
                  'Male',
                  'Other',
                  'Prefer not to say'
                ]"
                :error-messages="form.$getError('gender')"
                :loading="loading"
              />
            </div>

            <div>
              <label class="text-field-label">Pronoun</label>
              <v-select
                class="mt-2"
                flat
                solo
                v-model="form.pronoun"
                :items="['She/her/hers', 'He/him/his', 'They/them/theirs']"
                :error-messages="form.$getError('pronoun')"
                :loading="loading"
              />
            </div>
          </div>

          <div :class="form.id ? 'd-flex' : ''">
            <div>
              <label class="text-field-label">Birthdate (YYYY-MM-DD)</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.birthdate"
                :error-messages="form.$getError('birthdate')"
                :loading="loading"
              ></v-text-field>
            </div>

            <div class="ml-3" v-if="form.id">
              <label class="text-field-label">Age Group</label>
              <v-text-field
                flat
                solo
                required
                readonly
                class="mt-2"
                v-model="form.age_group"
                :error-messages="form.$getError('age_group')"
                :loading="loading"
              ></v-text-field>
            </div>
          </div>

          <label class="text-field-label">Description</label>
          <v-textarea
            flat
            solo
            required
            class="mt-2"
            v-model="form.description"
            :error-messages="form.$getError('description')"
            :loading="loading"
          ></v-textarea>

          <v-checkbox
            v-if="user"
            :input-value="user && user.blocked_at"
            label="Disable user account"
            color="orange darken-3"
            class="mt-0"
            @mousedown="showModal = true"
          ></v-checkbox>

          <div
            v-if="
              user && user.role === 'Mentee' && user.requires_age_disclaimer
            "
          >
            <v-divider></v-divider>

            <h3 class="py-5 primary--text">Guardian Information</h3>

            <div class="d-flex">
              <div class="mr-3">
                <label class="text-field-label">First Name</label>
                <v-text-field
                  flat
                  solo
                  class="mt-2"
                  v-model="form.guardian_first_name"
                  :error-messages="form.$getError('guardian_first_name')"
                  :loading="loading"
                ></v-text-field>
              </div>

              <div>
                <label class="text-field-label">Last Name</label>
                <v-text-field
                  flat
                  solo
                  class="mt-2"
                  v-model="form.guardian_last_name"
                  :error-messages="form.$getError('guardian_last_name')"
                  :loading="loading"
                ></v-text-field>
              </div>
            </div>

            <label class="text-field-label">Email</label>
            <v-text-field
              flat
              solo
              class="mt-2"
              v-model="form.guardian_email"
              :error-messages="form.$getError('guardian_email')"
              :loading="loading"
            ></v-text-field>

            <label class="text-field-label">Relationship</label>
            <v-select
              class="mt-2"
              flat
              solo
              v-model="form.guardian_relationship"
              :items="['Parent', 'Grandparent', 'Legal Guardian', 'Other']"
              :error-messages="form.$getError('guardian_relationship')"
              :loading="loading"
            />

            <label class="text-field-label">Phone number</label>
            <v-text-field
              class="mt-2"
              flat
              solo
              v-model="form.guardian_phone_number"
              :error-messages="form.$getError('guardian_phone_number')"
              :loading="loading"
            />
          </div>

          <div v-else-if="!user || (user && user.role === 'Mentor')">
            <v-divider></v-divider>

            <h3 class="py-5 primary--text">Qualifications</h3>

            <v-text-field
              flat
              solo
              class="mb-2"
              :hide-details="form.qualifications.length != 0"
              placeholder="Enter qualification"
              v-model="qualificationName"
              :loading="loading"
              @keydown.enter="onAddQualification"
            ></v-text-field>

            <v-list flat class="pa-0">
              <v-list-item
                class="pa-0"
                v-for="(qualification, qIndex) in form.qualifications"
                :key="qIndex"
              >
                <v-list-item-content class="px-0">
                  <v-list-item-title class="text-wrap">
                    <!-- eslint-disable-next-line -->
                    {{ qualification.title }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-icon
                  class="my-3"
                  @click="onRemoveQualification(qIndex)"
                >
                  <v-icon color="red">{{ icons.delete }}</v-icon>
                </v-list-item-icon>
              </v-list-item>
            </v-list>
          </div>

          <div class="mt-6">
            <v-btn
              :disabled="user && !!user.blocked_at"
              color="primary"
              class="mr-4 px-6"
              height="40px"
              width="100%"
              @click="user ? updateUserDetails() : addUser()"
              :loading="form.$busy"
              >Save</v-btn
            >
          </div>
        </v-form>

        <v-dialog v-model="deleteDialog" max-width="290">
          <v-card>
            <v-card-title class="headline">Confirm Delete</v-card-title>

            <v-card-text>Are you sure you want to delete this?</v-card-text>

            <v-card-actions>
              <v-btn text @click="deleteDialog = false" :loading="form.$busy"
                >Cancel</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn color="red" text @click="deleteUser" :loading="form.$busy"
                >Yes, Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <!-- <test-notifications v-if="user" class="pa-4" :user="user" /> -->

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>

    <ConfirmModal
      v-model="showModal"
      title="Disable Account"
      :message="disableMessage"
      @cancel="showModal = false"
      @confirm="blockAccount"
    ></ConfirmModal>
  </div>
</template>

<script>
import {
  mdiChevronLeft,
  mdiCheck,
  mdiAlertCircleOutline,
  mdiMinusCircle
} from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import AvatarForm from './components/AvatarForm'
// import TestNotifications from '@/components/TestNotifications'
import ConfirmModal from '@/components/modals/ConfirmModal'

import Form from '@/utils/form'

export default {
  name: 'UserDetails',

  components: {
    AvatarForm,
    AppBarNavIcon,
    // TestNotifications,
    ConfirmModal
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
        delete: mdiMinusCircle
      },
      loading: false,
      avatarLoading: false,
      deleteDialog: false,
      form: new Form({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        location: '',
        display_name: '',
        gender: '',
        pronoun: '',
        birthdate: '',
        description: '',
        avatar: '',
        guardian_first_name: '',
        guardian_last_name: '',
        guardian_email: '',
        guardian_relationship: '',
        guardian_phone_number: '',
        qualifications: []
      }),
      qualificationName: '',
      showCropperModal: false,
      avatarTimestamp: new Date().getTime(),
      snackbar: {
        show: false,
        message: null,
        color: ''
      },
      showModal: false,
      isLoadingDelete: false
    }
  },

  computed: {
    ...mapState({
      user: state => state.user.userDetails,
      authUser: state => state.auth.user
    }),
    displayName() {
      return `${this.form.first_name} ${this.form.last_name}` || this.form.email
    },
    disableMessage() {
      const action = this.user && this.user.blocked_at ? 'unblock' : 'block'
      return `Are you sure you want ${action} this account?`
    }
  },

  methods: {
    ...mapActions({
      getUserDetails: 'user/getUserDetails',
      changeAvatar: 'user/changeAvatar',
      updateUser: 'user/updateUser',
      blockUser: 'user/blockUser',
      registerMentor: 'user/registerMentor'
    }),

    ...mapMutations({
      clearUserDetails: 'user/clearUserDetails',
      setSelectedUserAvatar: 'user/setSelectedUserAvatar',
      userAvatarRemoved: 'user/userAvatarRemoved'
    }),

    async getUser() {
      this.loading = true
      await this.getUserDetails(this.$route.params.id)
      this.form = new Form(this.user)
      this.form.guardian_first_name = this.user.guardian
        ? this.user.guardian.first_name
        : ''
      this.form.guardian_last_name = this.user.guardian
        ? this.user.guardian.last_name
        : ''
      this.form.guardian_email = this.user.guardian
        ? this.user.guardian.email
        : ''
      this.form.guardian_relationship = this.user.guardian
        ? this.user.guardian.relationship
        : ''
      this.form.guardian_phone_number = this.user.guardian
        ? this.user.guardian.phone_number
        : ''

      this.form.qualifications = this.user.qualifications
        ? this.user.qualifications
        : []
      this.loading = false
    },

    async avatarChange(image) {
      this.avatarLoading = true
      let data = new FormData()
      data.append('user_id', this.user.id)
      data.append('avatar', image)
      await this.changeAvatar(data)
      this.avatarLoading = false
      this.showSnackbar('Avatar successfully updated!', 'success')
    },

    onUploadImage(blob, image) {
      this.form.avatar = blob
    },

    async addUser() {
      this.form.$busy = true
      this.form.$clearErrors()

      let data = new FormData()
      for (let key in this.form.$data()) {
        data.append(key, this.form[key])
      }
      if (this.form.qualifications.length !== 0) {
        let qualifications = this.form.qualifications.map(o => o.title)
        for (let i = 0; i < this.form.qualifications.length; i++) {
          data.append('qualifications[]', qualifications[i])
        }
      } else {
        data.delete('qualifications')
      }

      this.registerMentor(data)
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('Mentor successfully registered!', 'success')
          this.$router.replace({ name: 'users' })
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async updateUserDetails() {
      this.form.$busy = true
      this.form.$clearErrors()

      let temp = this.form.qualifications
      if (this.user.role === 'Mentor') {
        this.form.qualifications = this.form.qualifications.map(o => o.title)
      } else {
        delete this.form.qualifications
      }

      this.updateUser(this.form)
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('User details successfully updated!', 'success')
          this.$router.replace({ name: 'users' })
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
          this.form.qualifications = temp
        })
    },

    deleteUser() {
      this.isLoadingDelete = true
      this.user.delete().then(() => {
        this.isLoadingDelete = false
        this.deleteDialog = false
        this.showSnackbar('User deleted successfully!')
        this.$router.replace('/users')
      })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },

    blockAccount(value) {
      this.form.$busy = true
      const action = this.user.blocked_at ? 'unblocked' : 'blocked'
      this.blockUser({
        id: this.user.id,
        action
      })
        .then(() => {
          this.form.$busy = false
          this.showModal = false
          this.showSnackbar(`User successfully ${action}!`, 'success')
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    onAddQualification() {
      this.form.qualifications.push({ title: this.qualificationName })
      this.qualificationName = ''
    },

    onRemoveQualification(index) {
      this.form.qualifications.splice(index, 1)
    }
  },

  created() {
    if (this.$router.currentRoute.name === 'user.details') {
      this.getUser()
    }
  },

  destroyed() {
    this.clearUserDetails()
  },

  watch: {
    $route() {
      this.getUser()
    }
  }
}
</script>

<style lang="scss" scoped>
.user-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
